import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useScroll, useSize, useUpdateEffect } from 'ahooks';

import { useAppSelector } from 'store/hooks';
import { addressFormat } from 'lib/nicknameFormat';
import classNames from 'classnames';
import { Loader03 } from 'lib/lucky-x-icons-2';
import { ActiveRaffle } from 'api/project/project_detail';

interface IActiveRafflesProps {
  isChangeStyle?: boolean;
  projectActiveRaffle: ActiveRaffle[];
  requested: boolean;
}

const ActiveRaffles: React.FC<IActiveRafflesProps> = ({
  isChangeStyle,
  projectActiveRaffle,
  requested,
}) => {
  const { screenWidth } = useAppSelector((state) => state.nav);
  const list2Ref = useRef<HTMLDivElement>(null);
  const content2Ref = useRef(null);
  const listScroll = useScroll(list2Ref);
  const [isScroll, setIsScroll] = useState(false);
  const contentSize = useSize(content2Ref);

  useEffect(() => {
    if (listScroll?.top !== 0 && !!listScroll) {
      setIsScroll(true);
    }
  }, [listScroll, listScroll?.top]);

  useUpdateEffect(() => {
    if (isScroll) {
      setTimeout(() => {
        setIsScroll(false);
      }, 1000);
    }
  }, [isScroll, listScroll?.top]);

  const scrollToBottom = useMemo(() => {
    return Number(listScroll?.top) / (Number(contentSize?.height) - 150);
  }, [contentSize?.height, listScroll?.top]);

  const listRender = useCallback(() => {
    if (!requested) {
      return <Loader03 size={20} className="m-auto pt-5 text-neutral-100" />;
    } else if (!projectActiveRaffle || projectActiveRaffle?.length === 0) {
      return (
        <div
          className={classNames({
            'flex h-full items-center justify-center pr-5 text-center text-xs lg:text-sm':
              !isChangeStyle,
            'py-14 text-center text-xs': isChangeStyle,
          })}
        >
          No active raffles
        </div>
      );
    } else {
      return (
        <div
          className="relative h-[162px] overflow-y-scroll pt-3"
          ref={list2Ref}
        >
          <div
            className={classNames(
              'absolute right-1 hidden h-[110px] w-2 rounded-[49px] bg-neutral-700',
              {
                '!block': isScroll,
              }
            )}
            style={{
              top: Number(listScroll?.top) + 45 * scrollToBottom,
            }}
          ></div>
          <div className="grid grid-cols-1 gap-2 pr-5" ref={content2Ref}>
            {projectActiveRaffle?.slice(0, 50).map((item) => (
              <div
                className={classNames('flex gap-2', {
                  'flex-col': isChangeStyle || screenWidth < 768,
                  'flex-col md:flex-row md:items-center md:justify-between': !(
                    isChangeStyle || screenWidth < 768
                  ),
                })}
                key={item?.vanity_url}
              >
                <div className="flex items-center gap-2">
                  {item?.campaign?.logo ? (
                    <img
                      src={item?.campaign?.logo}
                      className="h-5 w-5 shrink-0 rounded-full"
                      alt=""
                      onError={(e: any) => {
                        e.target.src =
                          'https://s.luckygo.io/luckybuy/img/detailAvatarImg.png';
                      }}
                    />
                  ) : (
                    <div className="h-5 w-5 shrink-0 rounded-full bg-neutral-400 text-center text-sm leading-6 text-neutral-100">
                      {item?.campaign?.name?.slice(0, 1)}
                    </div>
                  )}
                  <div
                    className={classNames(
                      'cursor-pointer truncate  font-normal underline  md:max-w-[210px] lg:max-w-[320px]',
                      {
                        'text-xs': isChangeStyle,
                        'text-xs lg:text-sm': !isChangeStyle,
                      }
                    )}
                    onClick={() => {
                      open(`/r/${item?.vanity_url}`, '_self');
                    }}
                  >
                    {item?.name}
                  </div>
                </div>

                {!!item?.my_entry && (
                  <div className="flex items-center justify-center text-xs font-medium underline lg:text-sm">
                    {addressFormat(item?.my_entry?.mint_wallet)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
  }, [
    isChangeStyle,
    isScroll,
    listScroll?.top,
    projectActiveRaffle,
    requested,
    screenWidth,
    scrollToBottom,
  ]);

  return (
    <div className="w-1/2 pl-5">
      <div className=" text-base font-semibold">Active raffles</div>
      {listRender()}
    </div>
  );
};

export default ActiveRaffles;
